import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Send, Plus, Search, ArrowRight, Loader2, Paperclip, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const roleColor = { admin: "bg-navy text-gold", supervisor: "bg-emerald-600 text-white", teacher: "bg-amber-500 text-white", student: "bg-sky-500 text-white" };
const initials = (n) => (n || "؟").trim().charAt(0);
const timeShort = (iso) => { if (!iso) return ""; try { const d = new Date(iso); return d.toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const presenceText = (o) => (o?.online ? "متصل الآن" : (o?.last_seen ? `آخر ظهور ${timeShort(o.last_seen)}` : (o?.role_label || "")));

export default function Chat() {
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const loadConvs = useCallback(() => api.get("/chat/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([])), []);
  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 5000); return () => clearInterval(t); }, [loadConvs]);

  const startWith = async (contact) => {
    try {
      const r = await api.post("/chat/conversations", { target_id: contact.id });
      setNewOpen(false); setActive(r.data); loadConvs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (convs === null) return <PageLoader />;

  return (
    <div data-testid="chat-page" className="flex h-[calc(100vh-9rem)] overflow-hidden rounded-2xl border border-slate-100 bg-card soft-shadow">
      {/* Conversation list */}
      <div className={`flex w-full flex-col border-l border-slate-100 sm:w-80 ${active ? "hidden sm:flex" : "flex"}`}>
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <h2 className="font-heading text-lg font-bold text-navy">المحادثات</h2>
          <Button data-testid="new-chat-btn" onClick={() => setNewOpen(true)} size="sm" className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-1 h-4 w-4" /> جديدة</Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convs.length === 0 ? (
            <div className="p-6"><EmptyState icon={MessageCircle} title="لا توجد محادثات" desc="ابدأ محادثة جديدة للتواصل." /></div>
          ) : convs.map((c) => (
            <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => setActive(c)}
              className={`flex w-full items-center gap-3 border-b border-slate-50 p-3 text-start transition-colors hover:bg-slate-50 ${active?.id === c.id ? "bg-slate-50" : ""}`}>
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${roleColor[c.other.role] || "bg-slate-300 text-white"}`}>{initials(c.other.name)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-semibold text-navy">{c.other.name}</p>
                  <span className="shrink-0 text-[10px] text-slate-400">{timeShort(c.last_message_at)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-xs text-slate-400">{c.last_message || c.other.role_label}</p>
                  {c.unread > 0 && <span data-testid={`unread-${c.id}`} className="shrink-0 rounded-full bg-gold px-1.5 text-[10px] font-bold text-navy">{c.unread}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat window */}
      <div className={`flex-1 flex-col ${active ? "flex" : "hidden sm:flex"}`}>
        {active ? <ChatWindow key={active.id} conv={active} me={user} onBack={() => setActive(null)} onSent={loadConvs} />
          : <div className="flex h-full flex-col items-center justify-center text-slate-300"><MessageCircle className="h-16 w-16" /><p className="mt-3 text-sm">اختر محادثة لعرض الرسائل</p></div>}
      </div>

      <NewChatDialog open={newOpen} onClose={() => setNewOpen(false)} onPick={startWith} />
    </div>
  );
}

function ChatWindow({ conv, me, onBack, onSent }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);
  const countRef = useRef(0);
  const fileRef = useRef(null);
  const lastTypingRef = useRef(0);
  const token = localStorage.getItem("token");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/chat/conversations/${conv.id}/messages`);
      setData(r.data);
      if (r.data.messages.length !== countRef.current) {
        countRef.current = r.data.messages.length;
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    } catch (e) { /* ignore */ }
  }, [conv.id]);

  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);

  const emitTyping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current > 2500) {
      lastTypingRef.current = now;
      api.post(`/chat/conversations/${conv.id}/typing`).catch(() => {});
    }
  };

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true); setText("");
    try {
      await api.post(`/chat/conversations/${conv.id}/messages`, { text: t });
      await load(); onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setText(t); }
    finally { setSending(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("حجم الملف يتجاوز 25 ميجابايت"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (text.trim()) fd.append("caption", text.trim());
      await api.post(`/chat/conversations/${conv.id}/attachment`, fd);
      setText(""); await load(); onSent();
      toast.success("تم إرسال المرفق");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const attUrl = (m) => `${API}/chat/attachments/${m.id}/download?auth=${token}`;
  const other = data?.other || conv.other;

  return (
    <>
      <div className="flex items-center gap-3 border-b border-slate-100 p-4">
        <button onClick={onBack} className="sm:hidden" data-testid="chat-back"><ArrowRight className="h-5 w-5 text-slate-500" /></button>
        <div className="relative">
          <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${roleColor[other.role] || "bg-slate-300 text-white"}`}>{initials(other.name)}</div>
          {other.online && <span data-testid="presence-dot" className="absolute bottom-0 left-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />}
        </div>
        <div><p className="font-heading font-bold text-navy">{other.name}</p>
          <p data-testid="presence-text" className={`text-xs ${data?.other_typing ? "font-semibold text-greenx" : other.online ? "font-semibold text-emerald-600" : "text-slate-400"}`}>
            {data?.other_typing ? "يكتب الآن..." : presenceText(other)}</p></div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto bg-background/40 p-4" data-testid="messages-area">
        {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
          : data.messages.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">لا توجد رسائل بعد — ابدأ المحادثة</p>
          : data.messages.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "rounded-tr-sm bg-navy text-white" : "rounded-tl-sm bg-white text-navy ring-1 ring-slate-100"}`}>
                {m.attachment && (m.attachment.kind === "image" ? (
                  <a href={attUrl(m)} target="_blank" rel="noreferrer" data-testid={`att-img-${m.id}`}>
                    <img src={attUrl(m)} alt={m.attachment.name} className="mb-1 max-h-56 w-full rounded-lg object-cover" />
                  </a>
                ) : (
                  <a href={attUrl(m)} target="_blank" rel="noreferrer" data-testid={`att-file-${m.id}`}
                     className={`mb-1 flex items-center gap-2 rounded-lg px-2.5 py-2 ${m.mine ? "bg-white/15" : "bg-slate-50"}`}>
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="max-w-[150px] truncate text-xs">{m.attachment.name}</span>
                    <Download className="h-3.5 w-3.5 shrink-0" />
                  </a>
                ))}
                {m.text && <p className="whitespace-pre-line break-words">{m.text}</p>}
                <p className={`mt-1 text-[10px] ${m.mine ? "text-slate-300" : "text-slate-400"}`}>{timeShort(m.created_at)}</p>
              </div>
            </div>
          ))}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-2 border-t border-slate-100 p-3">
        <input ref={fileRef} type="file" data-testid="chat-file-input" className="hidden" onChange={onPickFile}
               accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.zip,.rar,.mp3,.mp4,.wav" />
        <Button data-testid="chat-attach" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" size="icon" className="shrink-0 rounded-xl border-slate-200">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4 text-slate-500" />}
        </Button>
        <Input data-testid="chat-input" value={text} onChange={(e) => { setText(e.target.value); emitTyping(); }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="اكتب رسالتك..." className="rounded-xl" />
        <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()} className="shrink-0 rounded-xl bg-greenx px-4 hover:bg-greenx-light">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </>
  );
}

function NewChatDialog({ open, onClose, onPick }) {
  const [contacts, setContacts] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { if (open) { setContacts(null); api.get("/chat/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([])); } }, [open]);
  const filtered = (contacts || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-hidden">
        <DialogHeader><DialogTitle className="font-heading text-navy">محادثة جديدة</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="contact-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم..." className="rounded-xl pr-9" />
        </div>
        <div className="max-h-[50vh] space-y-1 overflow-y-auto">
          {contacts === null ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            : filtered.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">لا توجد جهات متاحة للتواصل</p>
            : filtered.map((c) => (
              <button key={c.id} data-testid={`contact-${c.id}`} onClick={() => onPick(c)} className="flex w-full items-center gap-3 rounded-xl p-2.5 text-start transition-colors hover:bg-slate-50">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${roleColor[c.role] || "bg-slate-300 text-white"}`}>{initials(c.name)}</div>
                <div><p className="font-semibold text-navy">{c.name}</p><p className="text-xs text-slate-400">{c.sub}</p></div>
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
